import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Auth from 'j-toker';

import Select from 'components/ui/SelectField';

import { default as AntDrawer } from 'components/ui/Drawer';

import DebouncedSearch from 'components/ui/DebouncedSearch';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { MdChevronRight as ChevronRightIcon } from 'react-icons/md';
import CtaText from 'components/ui/CtaCard';
import StatusTag from 'components/ui/StatusTag';
import { getTranslatedString } from 'helpers/i18n';
import { media } from 'helpers/MediaHelper';
import { colors } from 'helpers/ColorHelper';
import { apiPost } from 'helpers/ApiHelper';

import { AddPurchaseOrderDrawer } from './AddPurchaseOrderDrawer';

const FullWidthDebouncedSearch = styled(DebouncedSearch)`
  width: 100%;
`;

const StyledFormContainer = styled.div`
  margin-bottom: 30px;
`;

const StyledInternalHeader = styled.div`
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const StyledFormRow = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const StyledFormRowSpaceAbove = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  margin-top: 30px;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const StyledFormItem = styled.div`
  width: 100%;
  padding: ${(props) => props.padding || '0px 8px'};
  text-align: ${(props) => props.textAlign || 'left'};
  margin: ${(props) => props.margin || '0px'};
`;

const StyledFormItemLabel = styled.div`
  margin-bottom: 5px;
`;

const StyledLinkedContainer = styled.div`
  margin-top: 15px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.LIGHT_PERIWINKLE};
  background-color: ${colors.WHITE};
`;

const StyledRightButton = styled(CtaText)`
  margin-left: 10px;
  margin-top: 2.5px;
  padding: 0px;
`;

const StyledRightA = styled.a`
  float: right;
  margin-right: 10px;
`;

const ColCutoff = styled(Col)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledCtaText = styled(CtaText)`
  padding: 0;
`;

export const LinkPurchaseOrders = ({
  propertyId,
  selectedPurchaseOrders,
  handleSelectPO,
  handleUnlinkPO,
  getVendors,
  vendors,
  showTitle = true,
  padding,
}) => {
  const [searchResult, setSearchResult] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [createPODrawerOpen, setCreatePODrawerOpen] = useState(false);

  const handleSearch = useCallback(
    (query = null) => {
      let searchParams = {
        property_ids: [propertyId],
      };
      if (query) {
        searchParams['query'] = query;
      }

      apiPost(`${Auth.getApiUrl()}/landlords/purchase_orders?subset=all`, searchParams)
        .done((response) => {
          setSearchResult(response.data);
          setSearchLoading(false);
        })
        .catch((error) => {
          setSearchLoading(false);
        });
    },
    [propertyId, setSearchResult, setSearchLoading]
  );

  useEffect(() => {
    if (propertyId) {
      handleSearch();
    }
  }, [propertyId, handleSearch]);

  const handleSelect = (value) => {
    let selectedPO = value;
    if (typeof selectedPO === 'string' || selectedPO instanceof String) {
      selectedPO = searchResult.find((po) => po.id === value);
      selectedPO = selectedPO.attributes;
    }
    handleSelectPO(selectedPO.id);
    handleSearch();
  };

  const openCreatePO = () => {
    if (vendors && vendors.length > 0) {
      setCreatePODrawerOpen(true);
    } else {
      getVendors().then(() => {
        setCreatePODrawerOpen(true);
      });
    }
  };

  const showLinkedPOs = selectedPurchaseOrders && selectedPurchaseOrders.length > 0;
  const selectedPurchaseOrderIds = (selectedPurchaseOrders || []).map((po) => parseInt(po.id));
  const purchaseOrdersToLink = searchResult.filter(
    (record) => !selectedPurchaseOrderIds.includes(parseInt(record.id))
  );
  return (
    <StyledFormContainer>
      <StyledFormRow>
        <StyledFormItem padding={padding}>
          {showTitle && (
            <StyledInternalHeader>
              {getTranslatedString('landlordPortal.linkPO.linkPurchaseOrders')}
            </StyledInternalHeader>
          )}
          <StyledFormItemLabel>
            {getTranslatedString('landlordPortal.linkPO.selectPurchaseOrders')}
          </StyledFormItemLabel>
          <FullWidthDebouncedSearch
            showSearch
            placeholder={getTranslatedString('landlordPortal.linkPO.searchOrSelect')}
            size="large"
            onSearch={handleSearch}
            beforeSearch={(query) => setSearchLoading(true)}
            onSelect={handleSelect}
            filterOption={false}
            notFoundContent={null}
            disabled={!propertyId}
            loading={searchLoading}
            showArrow={false}
            value={undefined}
          >
            {renderPurchaseOrderOptions(purchaseOrdersToLink)}
          </FullWidthDebouncedSearch>
          <div>
            {getTranslatedString('landlordPortal.linkPO.dontSee')}{' '}
            <StyledCtaText type="button" onClick={openCreatePO}>
              {getTranslatedString('landlordPortal.linkPO.createNew')}
            </StyledCtaText>
          </div>
        </StyledFormItem>
      </StyledFormRow>

      {showLinkedPOs && (
        <StyledFormRowSpaceAbove>
          <StyledFormItem>
            <StyledInternalHeader>
              {getTranslatedString('landlordPortal.linkPO.linkedPurchaseOrders')}
            </StyledInternalHeader>
            {selectedPurchaseOrders.map((selectedPO) => (
              <StyledLinkedContainer key={`linked_po_${selectedPO.id}`}>
                <Row gutter={16}>
                  <Col xs={24}>
                    <strong>{selectedPO.attributes.external_vendor_name}</strong>
                  </Col>
                  <ColCutoff xs={16} sm={18}>
                    {getTranslatedString('landlordPortal.linkPO.items', {
                      quantity: selectedPO.attributes.total_quantity,
                    })}
                  </ColCutoff>
                  <Col xs={8} sm={6}>
                    <StyledRightButton type="button" onClick={() => handleUnlinkPO(selectedPO.id)}>
                      {getTranslatedString('landlordPortal.linkPO.unlink')}
                    </StyledRightButton>
                    <StyledRightA
                      href={`/companies/purchase_orders?purchaseOrderId=${selectedPO.id}`}
                      target="_blank"
                    >
                      <ChevronRightIcon size={24} style={{ padding: 0 }} />
                    </StyledRightA>
                  </Col>
                  <Col xs={24}>
                    <StatusTag status={selectedPO.attributes.status} />
                  </Col>
                </Row>
              </StyledLinkedContainer>
            ))}
          </StyledFormItem>
        </StyledFormRowSpaceAbove>
      )}
      <AntDrawer
        maskClosable={false}
        keyboard
        visible={createPODrawerOpen}
        onClose={() => setCreatePODrawerOpen(false)}
        closable={false}
        width={600}
      >
        {createPODrawerOpen && (
          <AddPurchaseOrderDrawer
            toggleNewPurchaseOrder={() => setCreatePODrawerOpen(false)}
            selectedPropertyId={propertyId}
            disabledPropertySelect
            hideLinking
            onSuccess={handleSelect}
          />
        )}
      </AntDrawer>
    </StyledFormContainer>
  );
};

const renderPurchaseOrderOptions = (searchResults) => {
  if (searchResults.length === 0) {
    return (
      <Select.Option key="1" value="none">
        {getTranslatedString('landlordPortal.purchaseOrders.noneFound')}
      </Select.Option>
    );
  }

  return searchResults.map((record) => {
    let poNumber = '';
    if (record.attributes.po_number) {
      poNumber = `#${record.attributes.po_number} `;
    }

    return (
      <Select.Option key={`record_${record.id}`} value={record.id}>
        <Row gutter={16}>
          <Col xs={18}>
            {poNumber}
            {record.attributes.title}
          </Col>
          <Col xs={6}>
            <span style={{ float: 'right' }}>
              <StatusTag status={record.attributes.status} />
            </span>
          </Col>
          <ColCutoff xs={18}>
            <small>{record.attributes.external_vendor_name}</small>
          </ColCutoff>
        </Row>
      </Select.Option>
    );
  });
};
